/**
 * Calendar view for events: week selector with events grouped by day.
 * Native mobile app style.
 */

import { useMemo, useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconClock,
  IconCheck,
} from "../../../components/Icons.tsx";
import { haptic } from "../../../lib/telegram.ts";
import {
  getTodayDateKeyBangkok,
  getWeekStartDateKeyBangkok,
  addDaysToDateKey,
  getDayOfMonthAndWeekdayBangkok,
  formatTimeBangkok,
  type BangkokDateKey,
} from "../../../lib/bangkok-time.ts";
import { APP_TIMEZONE } from "../../../config.ts";
import {
  type AdminEventListItem,
  EVENT_STATUS_CONFIG,
  EVENT_VISIBILITY_CONFIG,
} from "./types.ts";

const WEEKDAYS_SHORT = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const WEEKDAYS_FULL = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
];
const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

const CALENDAR_WEEK_STORAGE_KEY = "admin_events_calendar_week";
const CALENDAR_SELECTED_STORAGE_KEY = "admin_events_calendar_selected";

function isValidDateKey(s: string): s is BangkokDateKey {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

interface WeekDaySelectorProps {
  weekDayDateKeys: BangkokDateKey[];
  selectedDateKey: BangkokDateKey | null; // null means whole week selected
  onSelectDateKey: (dateKey: BangkokDateKey | null) => void;
  eventsByDay: Map<string, AdminEventListItem[]>;
  todayDateKey: BangkokDateKey;
}

function WeekDaySelector({
  weekDayDateKeys,
  selectedDateKey,
  onSelectDateKey,
  eventsByDay,
  todayDateKey,
}: WeekDaySelectorProps) {
  const isWeekSelected = selectedDateKey === null;

  return (
    <div className="flex gap-1 xs:gap-1.5 lg-mobile:gap-2">
      {weekDayDateKeys.map((dateKey) => {
        const dayEvents = eventsByDay.get(dateKey) || [];
        const hasEvents = dayEvents.length > 0;
        const hasDrafts = dayEvents.some((e) => e.status === "DRAFT");
        const isSelected = dateKey === selectedDateKey;
        const isTodayDate = dateKey === todayDateKey;
        const { dayOfMonth, weekdayIndex } =
          getDayOfMonthAndWeekdayBangkok(dateKey);

        return (
          <button
            key={dateKey}
            type="button"
            onClick={() => {
              haptic("light");
              onSelectDateKey(dateKey);
            }}
            className={`
              flex-1 flex flex-col items-center py-2 xs:py-2.5 lg-mobile:py-3 px-1 
              rounded-xl lg-mobile:rounded-2xl transition-all relative
              ${
                isSelected
                  ? "bg-[var(--color-accent)] text-white shadow-md"
                  : isWeekSelected
                    ? hasEvents
                      ? "bg-blue-100 text-blue-900"
                      : "bg-slate-100 text-slate-600"
                    : hasEvents
                      ? "bg-blue-50 text-blue-900 hover:bg-blue-100"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }
              ${isTodayDate && !isSelected && !isWeekSelected ? "ring-2 ring-[var(--color-accent)] ring-offset-1" : ""}
            `}
          >
            <span className="text-[10px] xs:text-xs lg-mobile:text-sm font-medium opacity-70">
              {WEEKDAYS_SHORT[weekdayIndex]}
            </span>
            <span className="text-base xs:text-lg lg-mobile:text-xl font-bold mt-0.5">
              {dayOfMonth}
            </span>
            {hasEvents && (
              <span
                className={`
                  mt-1 w-1.5 h-1.5 lg-mobile:w-2 lg-mobile:h-2 rounded-full
                  ${isSelected ? "bg-white/80" : isWeekSelected ? "bg-blue-500" : hasDrafts ? "bg-amber-500" : "bg-blue-500"}
                `}
              />
            )}
            {dayEvents.length > 0 && !isSelected && !isWeekSelected && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {dayEvents.length > 9 ? "9+" : dayEvents.length}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface EventItemProps {
  event: AdminEventListItem;
}

function EventItem({ event }: EventItemProps) {
  const navigate = useNavigate();
  const timeStr = formatTimeBangkok(event.startsAt);
  const statusConfig = EVENT_STATUS_CONFIG[event.status];
  const visibilityConfig = EVENT_VISIBILITY_CONFIG[event.visibility];

  return (
    <button
      type="button"
      onClick={() => {
        haptic("light");
        navigate(`/admin/events/${event.id}`);
      }}
      className="w-full flex items-center gap-3 py-3 px-4 active:bg-slate-50 transition-colors text-left"
    >
      {/* Time column */}
      <div className="w-12 flex flex-col items-center justify-center shrink-0">
        <IconClock className="w-3.5 h-3.5 text-slate-400 mb-0.5" />
        <span className="text-[12px] font-semibold text-slate-700">
          {timeStr}
        </span>
      </div>

      {/* Image or placeholder */}
      {event.imageUrl ? (
        <img
          src={event.imageUrl}
          alt=""
          className="w-11 h-11 object-cover rounded-lg flex-shrink-0"
        />
      ) : (
        <div className="w-11 h-11 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <IconCalendar className="w-5 h-5 text-slate-300" />
        </div>
      )}

      {/* Event info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <h3 className="font-medium text-[14px] text-slate-900 truncate">
            {event.title}
          </h3>
          {event.featured && (
            <span className="text-[10px] text-amber-500">★</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium rounded ${statusConfig.bgColor} ${statusConfig.color}`}
          >
            {event.status === "PUBLISHED" && <IconCheck className="w-2 h-2" />}
            {statusConfig.label}
          </span>
          <span
            className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${visibilityConfig.bgColor} ${visibilityConfig.color}`}
          >
            {visibilityConfig.label}
          </span>
        </div>
      </div>

      <svg
        className="w-4 h-4 text-slate-300 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

interface DayAgendaProps {
  selectedDateKey: BangkokDateKey | null; // null means whole week
  events: AdminEventListItem[];
  todayDateKey: BangkokDateKey;
  weekLabel?: string;
}

function DayAgenda({
  selectedDateKey,
  events,
  todayDateKey,
  weekLabel,
}: DayAgendaProps) {
  const isWeekView = selectedDateKey === null;
  const dayName = selectedDateKey
    ? WEEKDAYS_FULL[
        getDayOfMonthAndWeekdayBangkok(selectedDateKey).weekdayIndex
      ]
    : "";
  const isTodayDate = selectedDateKey === todayDateKey;

  if (events.length === 0) {
    return (
      <div className="card p-6 text-center">
        <IconCalendar className="w-12 h-12 mx-auto mb-3 text-slate-200" />
        <p className="text-slate-500 text-sm">
          {isWeekView
            ? "Нет мероприятий на этой неделе"
            : isTodayDate
              ? "Сегодня нет мероприятий"
              : "Нет мероприятий"}
        </p>
        {!isWeekView && (
          <p className="text-slate-400 text-xs mt-1">{dayName}</p>
        )}
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h2 className="text-sm font-semibold text-slate-800">
          {isWeekView
            ? `Неделя ${weekLabel}`
            : isTodayDate
              ? "Сегодня"
              : dayName}
        </h2>
        <span className="text-xs text-slate-500">
          {events.length}{" "}
          {events.length === 1
            ? "мероприятие"
            : events.length < 5
              ? "мероприятия"
              : "мероприятий"}
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {events.map((event) => (
          <EventItem key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}

interface EventsCalendarViewProps {
  events: AdminEventListItem[];
  onRefresh: () => void;
}

export function EventsCalendarView({ events }: EventsCalendarViewProps) {
  const todayDateKey = getTodayDateKeyBangkok();
  const [weekStartDateKey, setWeekStartDateKey] = useState(() => {
    const saved = sessionStorage.getItem(CALENDAR_WEEK_STORAGE_KEY);
    if (saved && isValidDateKey(saved)) return saved;
    return getWeekStartDateKeyBangkok();
  });
  // null means whole week is selected
  const [selectedDateKey, setSelectedDateKey] = useState<BangkokDateKey | null>(
    () => {
      const saved = sessionStorage.getItem(CALENDAR_SELECTED_STORAGE_KEY);
      if (saved === "week") return null;
      if (saved && isValidDateKey(saved)) return saved;
      return getTodayDateKeyBangkok();
    },
  );

  // Save calendar state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(CALENDAR_WEEK_STORAGE_KEY, weekStartDateKey);
    sessionStorage.setItem(
      CALENDAR_SELECTED_STORAGE_KEY,
      selectedDateKey === null ? "week" : selectedDateKey,
    );
  }, [weekStartDateKey, selectedDateKey]);

  const weekDayDateKeys = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        addDaysToDateKey(weekStartDateKey, i),
      ),
    [weekStartDateKey],
  );

  // Group events by day
  const eventsByDay = useMemo(() => {
    const map = new Map<string, AdminEventListItem[]>();
    for (const event of events) {
      const date = new Date(event.startsAt);
      const dateKey = date.toLocaleDateString("sv-SE", {
        timeZone: APP_TIMEZONE,
      }) as BangkokDateKey;
      const existing = map.get(dateKey);
      if (existing) {
        existing.push(event);
      } else {
        map.set(dateKey, [event]);
      }
    }
    // Sort events within each day by time
    for (const dayEvents of map.values()) {
      dayEvents.sort(
        (a, b) =>
          new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
      );
    }
    return map;
  }, [events]);

  // Get events for selected day or whole week
  const selectedEvents = useMemo(() => {
    if (selectedDateKey === null) {
      // Whole week selected - get all events for the week
      const weekEvents: AdminEventListItem[] = [];
      for (const dateKey of weekDayDateKeys) {
        const dayEvents = eventsByDay.get(dateKey) || [];
        weekEvents.push(...dayEvents);
      }
      // Sort by date/time
      return weekEvents.sort(
        (a, b) =>
          new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
      );
    }
    return eventsByDay.get(selectedDateKey) || [];
  }, [selectedDateKey, eventsByDay, weekDayDateKeys]);

  // Week label
  const weekLabel = useMemo(() => {
    const startKey = weekStartDateKey;
    const endKey = addDaysToDateKey(weekStartDateKey, 6);
    const [, startM] = startKey.split("-").map(Number);
    const [, endM] = endKey.split("-").map(Number);
    const startDay = startKey.split("-")[2];
    const endDay = endKey.split("-")[2];
    if (startM === endM) {
      return `${startDay}–${endDay} ${MONTHS_GENITIVE[startM - 1]}`;
    }
    return `${startDay} ${MONTHS_GENITIVE[startM - 1]} – ${endDay} ${MONTHS_GENITIVE[endM - 1]}`;
  }, [weekStartDateKey]);

  const isWeekSelected = selectedDateKey === null;

  // Navigation handlers
  const goToPrevWeek = useCallback(() => {
    haptic("light");
    setWeekStartDateKey((prev) => addDaysToDateKey(prev, -7));
    if (selectedDateKey !== null) {
      setSelectedDateKey((prev) => (prev ? addDaysToDateKey(prev, -7) : null));
    }
  }, [selectedDateKey]);

  const goToNextWeek = useCallback(() => {
    haptic("light");
    setWeekStartDateKey((prev) => addDaysToDateKey(prev, 7));
    if (selectedDateKey !== null) {
      setSelectedDateKey((prev) => (prev ? addDaysToDateKey(prev, 7) : null));
    }
  }, [selectedDateKey]);

  const goToToday = useCallback(() => {
    haptic("medium");
    setWeekStartDateKey(getWeekStartDateKeyBangkok());
    setSelectedDateKey(getTodayDateKeyBangkok());
  }, []);

  const selectWholeWeek = useCallback(() => {
    haptic("medium");
    setSelectedDateKey(null);
  }, []);

  return (
    <div className="space-y-4">
      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={goToPrevWeek}
          className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 active:bg-slate-200 transition-colors"
        >
          <IconChevronLeft className="w-5 h-5" />
        </button>

        <button
          type="button"
          onClick={isWeekSelected ? goToToday : selectWholeWeek}
          className={`px-3 py-1.5 rounded-lg text-[13px] font-medium transition-all ${
            isWeekSelected
              ? "bg-[var(--color-accent)] text-white shadow-md"
              : "bg-slate-100 text-slate-700 active:bg-slate-200"
          }`}
        >
          {weekLabel}
        </button>

        <button
          type="button"
          onClick={goToNextWeek}
          className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 active:bg-slate-200 transition-colors"
        >
          <IconChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Week day selector */}
      <WeekDaySelector
        weekDayDateKeys={weekDayDateKeys}
        selectedDateKey={selectedDateKey}
        onSelectDateKey={setSelectedDateKey}
        eventsByDay={eventsByDay}
        todayDateKey={todayDateKey}
      />

      {/* Day agenda */}
      <DayAgenda
        selectedDateKey={selectedDateKey}
        events={selectedEvents}
        todayDateKey={todayDateKey}
        weekLabel={weekLabel}
      />
    </div>
  );
}
